import usePrivateAxios from '@/common/api/usePrivateAxios'
import { ErrorResponse } from '@/common/types/errorResponse'
import { toIsoFromDateTimeParts } from '@/common/utils/dateUtils'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import { UpdateExecutionEntryEstablishmentAcceptValidationSchema } from '../schemas/updateExecutionEntryEstablishmentAccept.schema'

interface UseUpdateMaintenanceExecutionEntryEstablishmentAcceptProps {
  establishmentChoiceEntryId: string | undefined
  options?: Partial<
    Omit<
      UseMutationOptions<unknown, ErrorResponse, UpdateExecutionEntryEstablishmentAcceptValidationSchema>,
      'mutationKey' | 'mutationFn'
    >
  >
}

const buildModification = (
  idKey: 'objective_piece_id' | 'objective_activity_id',
  item: UpdateExecutionEntryEstablishmentAcceptValidationSchema['pieces'][number],
) => {
  const newUnitPriceNumber = item.new_unit_price ? unFormatCurrency(item.new_unit_price) : null
  const newWarrantyNumber = item.new_warranty_in_days ? unFormatUnknownUnit(item.new_warranty_in_days) : null

  const originalUnitPriceNumber = Number(item.original_unit_price)

  const unitPriceChanged = newUnitPriceNumber !== null && newUnitPriceNumber !== originalUnitPriceNumber
  const warrantyChanged = newWarrantyNumber !== null && newWarrantyNumber !== item.original_warranty_in_days

  if (!unitPriceChanged && !warrantyChanged) {
    return null
  }

  return {
    [idKey]: item.id,
    new_unit_price: unitPriceChanged ? newUnitPriceNumber : undefined,
    new_warranty_in_days: warrantyChanged ? newWarrantyNumber : undefined,
  }
}

export const useUpdateMaintenanceExecutionEntryEstablishmentAccept = ({
  establishmentChoiceEntryId,
  options,
}: UseUpdateMaintenanceExecutionEntryEstablishmentAcceptProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['maintenanceExecutionEntryEstablishmentChoiceManualEntryProposalEstablishmentAccept'],
    mutationFn: (form) => {
      const pieces_modifications_attributes = form.pieces
        .map((piece) => buildModification('objective_piece_id', piece))
        .filter((modification) => modification !== null)

      const activities_modifications_attributes = form.activities
        .map((activity) => buildModification('objective_activity_id', activity))
        .filter((modification) => modification !== null)

      const payload = {
        start_at: toIsoFromDateTimeParts(form.start_at_date, form.start_at_time),
        deadline_to_complete_in_days: unFormatUnknownUnit(form.deadline_to_complete_in_days),
        pieces_modifications_attributes,
        activities_modifications_attributes,
      }

      return privateAxios.put(
        `/maintenance/establishment_choice/manual_entry_proposals/${establishmentChoiceEntryId}/establishment_accept`,
        {
          directive_proposal_content_attributes: payload,
        },
      )
    },
    ...options,
  })
}
